import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useSearchStore from '../../store/useSearchStore';
import {
  SidebarContainer,
  FilterHeader,
  FilterTitle,
  ResetButton,
  ResultCount,
} from './SearchSidebar.styled';
import FilterSection from './components/FilterSection';
import FilterCheckbox from './components/FilterCheckbox';
import PriceFilter from './components/PriceFilter';
import SortDropdown from './components/SortDropdown';
import { getCurrencySymbol } from '../../lib/formatCurrency';

const getSeasonTranslationKey = (seasonName) => {
  const seasonMap = {
    'summer': 'summer',
    'winter': 'winter',
    'all season': 'allSeason',
    'all-season': 'allSeason',
  };
  return seasonMap[seasonName?.toLowerCase()] || seasonName;
};

const SearchSidebar = () => {
  const { t } = useTranslation();
  // Get state and actions from store
  const {
    tyres,
    seasonFilters,
    brandFilters,
    minPrice,
    maxPrice,
    sortValue,
    searchOnlyInStock,
    loadIndexFilters,
    speedRatingFilters,
    fuelEfficiencyFilters,
    widthFilters,
    profileFilters,
    rimSizeFilters,
    setSeasonFilter,
    setBrandFilter,
    setMinPrice,
    setMaxPrice,
    setSortValue,
    setSearchOnlyInStock,
    setLoadIndexFilter,
    setSpeedRatingFilter,
    setFuelEfficiencyFilter,
    setWidthFilter,
    setProfileFilter,
    setRimSizeFilter,
    resetFilters,
    fetchTyres,
  } = useSearchStore();

  const resultCount = tyres.length;

  // Handlers that trigger search after filter change
  const handleSeasonChange = useCallback(
    (seasonId, checked) => {
      setSeasonFilter(seasonId, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setSeasonFilter, fetchTyres]
  );

  const handleBrandChange = useCallback(
    (brandId, checked) => {
      setBrandFilter(brandId, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setBrandFilter, fetchTyres]
  );

  const handleSortChange = useCallback(
    (value) => {
      setSortValue(value);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setSortValue, fetchTyres]
  );

  const handleSearchOnlyInStockChange = useCallback(
    (checked) => {
      setSearchOnlyInStock(checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setSearchOnlyInStock, fetchTyres]
  );

  const handlePriceFilterApply = useCallback(() => {
    fetchTyres({ refresh: true });
  }, [fetchTyres]);

  const handleLoadIndexChange = useCallback(
    (value, checked) => {
      setLoadIndexFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setLoadIndexFilter, fetchTyres]
  );

  const handleSpeedRatingChange = useCallback(
    (value, checked) => {
      setSpeedRatingFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setSpeedRatingFilter, fetchTyres]
  );

  const handleFuelEfficiencyChange = useCallback(
    (value, checked) => {
      setFuelEfficiencyFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setFuelEfficiencyFilter, fetchTyres]
  );

  const handleWidthChange = useCallback(
    (value, checked) => {
      setWidthFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setWidthFilter, fetchTyres]
  );

  const handleProfileChange = useCallback(
    (value, checked) => {
      setProfileFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setProfileFilter, fetchTyres]
  );

  const handleRimSizeChange = useCallback(
    (value, checked) => {
      setRimSizeFilter(value, checked);
      setTimeout(() => fetchTyres({ refresh: true }), 0);
    },
    [setRimSizeFilter, fetchTyres]
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
    setTimeout(() => fetchTyres({ refresh: true }), 0);
  }, [resetFilters, fetchTyres]);

  const getResultText = (count) => {
    const displayCount = count >= 30 ? '30+' : count;
    let resultWord;

    if (count === 1) {
      resultWord = t('resultSingular');
    } else if (count === 2) {
      resultWord = t('resultDual');
    } else if (count === 3 || count === 4) {
      resultWord = t('resultTriQuad');
    } else {
      resultWord = t('resultPlural');
    }

    return `${displayCount} ${resultWord}`;
  };

  return (
    <SidebarContainer>
      <FilterHeader>
        <FilterTitle>
          <i className="mdi mdi-filter-variant" />
          {t('filter')}
        </FilterTitle>
        <ResetButton onClick={handleResetFilters}>
          <i className="mdi mdi-refresh" />
          {t('reset')}
        </ResetButton>
      </FilterHeader>

      <ResultCount>{getResultText(resultCount)}</ResultCount>

      <SortDropdown value={sortValue} onChange={handleSortChange} />

      <FilterSection title={t('availability')} defaultOpen={true}>
        <FilterCheckbox
          label={t('onlyInStock')}
          checked={searchOnlyInStock}
          onChange={handleSearchOnlyInStockChange}
        />
      </FilterSection>

      <FilterSection title={t('season')} defaultOpen={true}>
        {seasonFilters.map((filter) => (
          <FilterCheckbox
            key={filter.id}
            label={t(getSeasonTranslationKey(filter.name))}
            checked={filter.selected}
            onChange={(checked) => handleSeasonChange(filter.id, checked)}
          />
        ))}
      </FilterSection>

      <FilterSection title={t('brands')} defaultOpen={true}>
        {brandFilters.map((filter) => (
          <FilterCheckbox
            key={filter.value}
            label={filter.label}
            checked={filter.selected}
            onChange={(checked) => handleBrandChange(filter.value, checked)}
          />
        ))}
      </FilterSection>

      <FilterSection title={`${t('priceLabel')} (${getCurrencySymbol()})`} defaultOpen={true}>
        <PriceFilter
          minPrice={minPrice}
          maxPrice={maxPrice}
          onMinPriceChange={setMinPrice}
          onMaxPriceChange={setMaxPrice}
          onApply={handlePriceFilterApply}
        />
      </FilterSection>

      {loadIndexFilters && loadIndexFilters.length > 0 && (
        <FilterSection title={t('loadIndex')} defaultOpen={false}>
          {loadIndexFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) =>
                handleLoadIndexChange(filter.value, checked)
              }
            />
          ))}
        </FilterSection>
      )}

      {speedRatingFilters && speedRatingFilters.length > 0 && (
        <FilterSection title={t('speedRating')} defaultOpen={false}>
          {speedRatingFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) =>
                handleSpeedRatingChange(filter.value, checked)
              }
            />
          ))}
        </FilterSection>
      )}

      {fuelEfficiencyFilters && fuelEfficiencyFilters.length > 0 && (
        <FilterSection title={t('fuelEfficiency')} defaultOpen={false}>
          {fuelEfficiencyFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) =>
                handleFuelEfficiencyChange(filter.value, checked)
              }
            />
          ))}
        </FilterSection>
      )}

      {widthFilters && widthFilters.length > 0 && (
        <FilterSection title={t('tyreWidth')} defaultOpen={false}>
          {widthFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) => handleWidthChange(filter.value, checked)}
            />
          ))}
        </FilterSection>
      )}

      {profileFilters && profileFilters.length > 0 && (
        <FilterSection title={t('profile')} defaultOpen={false}>
          {profileFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) => handleProfileChange(filter.value, checked)}
            />
          ))}
        </FilterSection>
      )}

      {rimSizeFilters && rimSizeFilters.length > 0 && (
        <FilterSection title={t('rimSize')} defaultOpen={false}>
          {rimSizeFilters.map((filter) => (
            <FilterCheckbox
              key={filter.value}
              label={filter.label}
              checked={filter.selected}
              onChange={(checked) => handleRimSizeChange(filter.value, checked)}
            />
          ))}
        </FilterSection>
      )}
    </SidebarContainer>
  );
};

export default SearchSidebar;
